import { t } from '@lingui/core/macro';
import { Box, styled } from '@mui/material';
import { ImageOffIcon } from 'lucide-react';
import React, { type JSX } from 'react';

import { colorSystem } from '../../theme';
import { Dialog } from '../Dialog';
import { StandardLayoutProps } from '../types';

export interface ThumbnailProps extends StandardLayoutProps {
    src?: string;
    /** Sets `width` and `minWidth` of the thumbnail container. */
    width?: string;
    /** Sets `height` and `minHeight` of the thumbnail container. */
    height?: string;
    fallback?: JSX.Element;
    ImageProps?: React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>;
}

export function Thumbnail({ src, fallback, width, height, ImageProps, sx, ...rest }: ThumbnailProps): JSX.Element {
    const [isDialogOpen, setOpen] = React.useState(false);
    const state = useThumbnailState(src);

    if (state.status === 'error') {
        return fallback ?? <DefaultFallback width={width} height={height} />;
    }

    return (
        <>
            {isDialogOpen && (
                <Dialog maxWidth="md" open={true} title="" onClose={() => setOpen(false)}>
                    <img alt={t`Enlarged view`} style={{ maxHeight: '70vh', objectFit: 'contain' }} src={src} />
                </Dialog>
            )}
            <Container
                sx={{
                    width,
                    height,
                    minWidth: width,
                    minHeight: height,
                    boxSizing: 'border-box',
                    ...sx,
                }}
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setOpen(true);
                }}
                {...rest}
            >
                {state.status === 'ok' && (
                    <div
                        {...ImageProps}
                        data-testid="thumbnail-image"
                        style={{
                            ...ImageProps?.style,
                            width: '100%',
                            height: '100%',
                            backgroundImage: `url(${state.src})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat',
                        }}
                    />
                )}
                {!src && fallback && (
                    <div
                        style={{
                            width: '100%',
                            height: '100%',
                            background: colorSystem.neutral[1],
                            border: `1px solid ${colorSystem.neutral[2]}`,
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        {fallback}
                    </div>
                )}
            </Container>
        </>
    );
}

function useThumbnailState(src?: string) {
    const [state, setState] = React.useState<
        { status: 'loading' } | { status: 'ok'; src: string } | { status: 'error' }
    >({ status: 'loading' });

    React.useEffect(() => {
        if (!src) {
            return;
        }
        const img = new Image();
        img.onerror = () => {
            setState({ status: 'error' });
        };
        img.onload = () => {
            setState({ status: 'ok', src });
        };
        img.src = src;
    }, [src]);

    return state;
}

const Container = styled(Box)({
    borderRadius: '4px',
    cursor: 'pointer',
    boxSizing: 'border-box',
});

function DefaultFallback({ width, height }: { width?: string; height?: string }) {
    return (
        <div
            data-testid="broken-image-icon"
            style={{
                display: 'grid',
                placeItems: 'center',
                width,
                height,
                background: colorSystem.neutral[1],
                border: `1px solid ${colorSystem.neutral[2]}`,
                borderRadius: '4px',
            }}
        >
            <ImageOffIcon size={24} color={colorSystem.neutral[5]} />
        </div>
    );
}
